import { Box, SelectChangeEvent } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { member as memberApi } from '../../api';
import { DEFAULT_PAGINATION_FILTER } from '../../constants';
import { useApi, useAuth, useEnumList, useInternationalization } from '../../hooks';
import { MemberChangeRequestEntity } from '../../models';
import {
  ChangeRequestState,
  MemberChangeRequestEntityType,
  MemberChangeRequestFilter,
  OrderBy,
  PermissionKey,
  Styles,
} from '../../types';
import { formatShortDate } from '../../utils/helper';
import { FilterContainer } from '../Container';
import { Autocomplete, Select } from '../Form';
import { LayoutPage } from '../Layout';
import { MenuItem } from '../Menu';
import { RequirePermission } from '../Permission';
import { Table, TableColumn } from '../Table';
import { MemberChangeRequest } from './MemberChangeRequest';

const style: Styles = {
  selectSmallContainer: {
    width: {
      xs: '100%',
      md: '220px',
    },
  },
  selectLargeContainer: {
    width: {
      xs: '100%',
      md: '320px',
    },
  },
};

const defaultFilter: MemberChangeRequestFilter = {
  memberId: undefined,
  memberNameOrNumber: undefined,
  entityType: undefined,
  state: ChangeRequestState.Submitted,
};

export const MemberChangeRequests = () => {
  const { hasPermissions } = useAuth();
  const { t } = useInternationalization();
  const [entity, setEntity] = useState<MemberChangeRequestEntity>();
  const [filter, setFilter] = useState<MemberChangeRequestFilter>({
    ...defaultFilter,
    ...DEFAULT_PAGINATION_FILTER,
  });
  const { data, isLoading, refresh } = useApi(memberApi.getAllChangeRequests, null, filter);
  const stateOptions = useEnumList(ChangeRequestState, 'changeRequest:state', true);

  const entityTypeOptions = useEnumList(MemberChangeRequestEntityType, 'changeRequest:memberEntityType', true);

  const canApprove = useCallback(
    (value: MemberChangeRequestEntityType) => {
      return (
        (value === MemberChangeRequestEntityType.MemberGeneralInformation &&
          hasPermissions(PermissionKey.MemberApproveMembers)) ||
        (value === MemberChangeRequestEntityType.MemberContact && hasPermissions(PermissionKey.MemberApproveContacts))
      );
    },
    [hasPermissions],
  );

  const filteredEntityTypeOptions = useMemo(
    () => entityTypeOptions.filter((option) => !option.value || canApprove(option.value)),
    [entityTypeOptions, canApprove],
  );

  const onPaginationChange = (pageNumber: number, pageSize: number) =>
    setFilter((prev) => ({ ...prev, pageNumber: prev.pageSize !== pageSize ? 1 : pageNumber, pageSize }));

  const onSortChange = (orderBy: OrderBy[]) =>
    setFilter((prev) => ({ ...prev, orderBy: orderBy.length ? orderBy : undefined }));

  const onClear = () => {
    setFilter((prev) => ({ ...prev, ...defaultFilter }));
  };

  const onStateChange = (event: SelectChangeEvent<string>) => {
    setFilter((prev) => ({ ...prev, state: (event.target.value as ChangeRequestState) || undefined, pageNumber: 1 }));
  };

  const onEntityTypeChange = (event: SelectChangeEvent<string>) => {
    setFilter((prev) => ({
      ...prev,
      entityType: (event.target.value as MemberChangeRequestEntityType) || undefined,
      pageNumber: 1,
    }));
  };

  const renderSearch = () => (
    <FilterContainer onClear={onClear} mb={3}>
      <Box sx={style.selectLargeContainer}>
        <Autocomplete
          minSearchTextLength={1}
          value={filter.memberNameOrNumber}
          noOptionsText={t('member:search.notFound')}
          apiFunction={memberApi.getSuggestions}
          label={t('changeRequest:search.memberNameOrNumber')}
          placeholder={t('member:search.placeholder')}
          getOptionLabel={(option) => {
            if (typeof option === 'string') {
              return option;
            }
            return `${option.fullNumber ? option.fullNumber + ' - ' : ''}${option.name}`;
          }}
          onApplySearch={(searchText) =>
            setFilter((prev) => ({ ...prev, memberNameOrNumber: searchText ?? undefined, pageNumber: 1 }))
          }
          onSelectItem={(item) =>
            setFilter((prev) => ({ ...prev, memberId: item?.id, memberNameOrNumber: item?.name, pageNumber: 1 }))
          }
        />
      </Box>
      <Box sx={style.selectSmallContainer}>
        <Select
          displayEmpty
          options={stateOptions}
          value={filter.state ?? ''}
          onChange={onStateChange}
          label={t('changeRequest:search.state')}
        />
      </Box>
      <Box sx={style.selectLargeContainer}>
        <Select
          displayEmpty
          options={filteredEntityTypeOptions}
          value={filter.entityType ?? ''}
          onChange={onEntityTypeChange}
          label={t('changeRequest:search.entityType')}
        />
      </Box>
    </FilterContainer>
  );

  const renderActionsMenuItems = (e: MemberChangeRequestEntity, onClick: () => void) => [
    ...(e.state === ChangeRequestState.Submitted
      ? [
          <MenuItem
            id="review"
            key="review"
            onClick={() => {
              onClick();
              setEntity(e);
            }}
          >
            {t('changeRequest:actions.review')}
          </MenuItem>,
        ]
      : []),
  ];

  const renderMember = (entity: MemberChangeRequestEntity) => entity.member.name;

  const renderState = (entity: MemberChangeRequestEntity) => t(`changeRequest:state.${entity.state}`);

  const renderType = (entity: MemberChangeRequestEntity) => t(`changeRequest:type.${entity.type}`);

  const onCloseDrawer = () => {
    refresh();
    setEntity(undefined);
  };

  const onCancelDrawer = () => {
    setEntity(undefined);
  };

  const renderEntityType = (entity: MemberChangeRequestEntity) =>
    t(`changeRequest:memberEntityType.${entity.entityType}`);

  return (
    <RequirePermission keys={[PermissionKey.MemberApproveMembers, PermissionKey.MemberApproveContacts]} any>
      <LayoutPage display="Tab" title={t('changeRequest:sections.member')}>
        <>
          {renderSearch()}
          <Table
            data={data ?? []}
            translationNamespace="changeRequest"
            paginationFilter={filter}
            onPaginationChange={onPaginationChange}
            sortColumns={filter.orderBy}
            onSortChange={onSortChange}
            isLoading={isLoading}
            actionMenuItems={renderActionsMenuItems}
          >
            <TableColumn type="custom" width="25%" id="member" sortable render={renderMember} />
            <TableColumn type="custom" width="20%" id="entityType" sortable render={renderEntityType} />
            <TableColumn type="custom" width="10%" id="type" sortable render={renderType} />
            <TableColumn
              type="custom"
              sortable
              width="10%"
              id="createdDate"
              render={({ createdDate }: MemberChangeRequestEntity) => formatShortDate(createdDate)}
            />
            <TableColumn type="property" sortable width="10%" id="createdBy" align="center" />
            <TableColumn
              type="custom"
              sortable
              width="10%"
              id="reviewedDate"
              render={({ reviewedDate }: MemberChangeRequestEntity) => formatShortDate(reviewedDate)}
            />
            <TableColumn type="property" sortable width="10%" id="reviewedBy" align="center" />
            <TableColumn type="custom" width="15%" id="state" sortable render={renderState} />
          </Table>
        </>
        {entity && (
          <MemberChangeRequest memberChangeRequest={entity} onConfirm={onCloseDrawer} onCancel={onCancelDrawer} />
        )}
      </LayoutPage>
    </RequirePermission>
  );
};
